import * as React from "react";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";

interface ScoreSliderProps {
  value?: number;
  onChange?: (value: number) => void;
  onBlur?: () => void;
  disabled?: boolean;
  min?: number;
  max?: number;
  step?: number;
  showValue?: boolean;
  className?: string;
  id?: string;
  name?: string;
  "aria-label"?: string;
  "aria-describedby"?: string;
}

const ScoreSlider = React.forwardRef<HTMLDivElement, ScoreSliderProps>(
  (
    {
      value = 0,
      onChange,
      onBlur,
      disabled = false,
      min = 0,
      max = 100,
      step = 1,
      showValue = true,
      className,
      id,
      name,
      "aria-label": ariaLabel,
      "aria-describedby": ariaDescribedBy,
    },
    ref
  ) => {
    // Handle the slider value change
    const handleValueChange = React.useCallback(
      (newValue: number[]) => {
        // Slider returns an array, we only use single value
        if (onChange && newValue.length > 0) {
          onChange(newValue[0]);
        }
      },
      [onChange]
    );

    return (
      <div className={cn("space-y-2", className)} ref={ref}>
        <div className="flex items-center gap-4">
          <Slider
            id={id}
            name={name}
            value={[value]}
            onValueChange={handleValueChange}
            onBlur={onBlur}
            disabled={disabled}
            min={min}
            max={max}
            step={step}
            aria-label={ariaLabel || `Score slider from ${min} to ${max}`}
            aria-describedby={ariaDescribedBy}
            className="flex-1"
          />
          {showValue && (
            <div className="w-12 text-center">
              <span className="text-sm font-medium tabular-nums">
                {value}
              </span>
            </div>
          )}
        </div>
        {showValue && (
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{min}</span>
            <span>{max}</span>
          </div>
        )}
      </div>
    );
  }
);

ScoreSlider.displayName = "ScoreSlider";

export { ScoreSlider };